// The menu scene (plan 17): pages of yellow bubble-text planes anchored to
// cube faces, the BUGGS! logo above, drawn through the existing
// `ModelRenderer` with the alpha cutout. A page carries a yaw and each item a
// local transform; item world = cubeModel x rotationY(pageYaw) x itemLocal,
// the same `model` matrix everything else uses, so the pages ride every
// quarter-turn for free — exactly like the original's child transforms.
//
// Picking is shell-side screen space, not a sim ray: the active page projects
// each item's rest-pose AABB corners through the frame's own matrices, takes
// the 2D bounding rect, pads it, and a tap hit-tests rects in stack order.

import { ModelRenderer, poseWorlds, type CookedModel, type ModelBounds } from "../model";
import { multiply, rotationY, scaling, translation, type Mat4 } from "../math";
import { toScreen, type ViewRect } from "./view";
import type { MenuModelName, ModelName } from "./model-catalog";
import type { MenuItemName, MenuPage, ShellState } from "../shell/shell";
import { PAGE_ITEMS } from "../shell/shell";

const ITEM_MODEL: Record<MenuItemName, MenuModelName> = {
  start: "menuStart",
  howto: "menuHowto",
  options: "menuOptions",
  highscore: "menuHighscore",
  easy: "menuEasy",
  normal: "menuNormal",
  hard: "menuHard",
  back: "menuBack",
};

// Layout: the original scene's proportions, converted at (our half-cube
// 0.5) / (its 7.5). Items sit at y 2.5 / 0 / -2.5 / -5 there — stacked low,
// leaving the face's TOP for the logo. The cooked item quads are 13.65 x
// 1.96 cooked units in the model x-y plane; the cube face is 1.0 world
// unit, so ITEM_SCALE puts an item at ~0.88 of a face width. Everything
// hovers just in front of the face plane (0.53 vs its 7.55/7.5) so bugs
// walk behind the text.
const ITEM_SCALE = 0.065;
const ITEM_HOVER_Z = 0.505;
const ITEM_YS = [0.167, 0, -0.167, -0.333] as const;
// The title copies live on the same hover plane as their page's items, at
// y = 6 there — OVERLAPPING the face's upper rows (drawn over the grass,
// being in front), fully inside the face outline. What keeps the four
// copies out of each other's way is geometry, and it constrains all three
// numbers here:
//   1. adjacent pages' planes are perpendicular, so quads intersect if a
//      half-width exceeds the hover distance (an earlier title z of 0.28 <
//      0.43 half-width cut a sliver through the visible title);
//   2. a neighbouring copy is hidden behind the cube's own side face only
//      while hover/face <= (camDist - halfWidth)/(camDist - face) — our
//      camera sits proportionally close (2.2 vs their 25.7/7.5), so the
//      old 0.53 hover (6% of the face, vs the original's 0.7%) let the
//      neighbour's near edge slide out past the right silhouette;
//   3. nothing may rise past the top edge (y + halfHeight <= 0.5), or the
//      neighbours' top strips peek over the cube (the y 0.62 attempt) —
//      which also bounds the original's own logo to halfHeight <= ~1.5,
//      smaller than the marketing artwork suggests.
const TITLE_SCALE = 0.7;
const TITLE_Y = 0.4;
const TITLE_Z = ITEM_HOVER_Z;

/** Fraction added to each side of an item's screen rect before hit-testing. */
const RECT_PAD = 0.1;

/**
 * The page yaws. `main` faces the resting camera (+Z); `difficulty` sits one
 * clockwise turn away on +X — `rotationY(pi/2)` maps +Z to +X, and the
 * shell's CLOCKWISE turn (`rotationY(-pi/2)`) cancels it exactly when the
 * face arrives, so the page reads upright with zero special cases.
 */
const PAGE_YAW: Record<MenuPage, number> = { main: 0, difficulty: Math.PI / 2 };

export function pageMatrix(page: MenuPage): Mat4 {
  return rotationY(PAGE_YAW[page]);
}

/**
 * An item's face-local transform. The cooked quads read correctly from their
 * -z side (audited in the §1 pass), so a half-turn about Y faces the text at
 * the camera the page fronts.
 */
function itemLocal(index: number): Mat4 {
  return multiply(
    translation(0, ITEM_YS[index], ITEM_HOVER_Z),
    multiply(rotationY(Math.PI), scaling(ITEM_SCALE)),
  );
}

const TITLE_LOCAL: Mat4 = multiply(
  translation(0, TITLE_Y, TITLE_Z),
  multiply(rotationY(Math.PI), scaling(TITLE_SCALE)),
);

/** Item -> its world matrix (before the cube model), in stack order. */
export function itemWorlds(page: MenuPage): readonly { item: MenuItemName; world: Mat4 }[] {
  const pageM = pageMatrix(page);
  return PAGE_ITEMS[page].map((item, i) => ({ item, world: multiply(pageM, itemLocal(i)) }));
}

export interface MenuScreenRect {
  readonly item: MenuItemName;
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * The padded client-space bounding rect of `bounds` under `world`, or null
 * when any corner projects behind the camera. Pure over the projection
 * helper, so the hit-test is testable with synthetic bounds.
 */
export function boundsScreenRect(
  bounds: ModelBounds,
  world: Mat4,
  rect: ViewRect,
): { left: number; top: number; width: number; height: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let corner = 0; corner < 8; corner++) {
    const p: [number, number, number] = [
      (corner & 1) === 0 ? bounds.min[0] : bounds.max[0],
      (corner & 2) === 0 ? bounds.min[1] : bounds.max[1],
      (corner & 4) === 0 ? bounds.min[2] : bounds.max[2],
    ];
    const at = toScreen(p, world, rect);
    if (at === null) return null;
    if (at.x < minX) minX = at.x;
    if (at.y < minY) minY = at.y;
    if (at.x > maxX) maxX = at.x;
    if (at.y > maxY) maxY = at.y;
  }
  const padX = (maxX - minX) * RECT_PAD;
  const padY = (maxY - minY) * RECT_PAD;
  return {
    left: minX - padX,
    top: minY - padY,
    width: maxX - minX + 2 * padX,
    height: maxY - minY + 2 * padY,
  };
}

/** Top-most first: rects are tested in the order given, first hit wins. */
export function hitTestRects(rects: readonly MenuScreenRect[], x: number, y: number): MenuItemName | null {
  for (const r of rects) {
    if (x >= r.left && x <= r.left + r.width && y >= r.top && y <= r.top + r.height) return r.item;
  }
  return null;
}

export class MenuRenderer {
  constructor(private readonly models: Record<ModelName, CookedModel>) {}

  /**
   * The active page's tappable rects — the same rects `buggsDebug.menu()`
   * reports, so shot tools tap exactly what picking sees.
   */
  itemRects(page: MenuPage, cubeModel: Mat4, rect: ViewRect): readonly MenuScreenRect[] {
    const out: MenuScreenRect[] = [];
    for (const { item, world } of itemWorlds(page)) {
      const model = this.models[ITEM_MODEL[item]];
      const r = boundsScreenRect(model.bounds, multiply(cubeModel, world), rect);
      if (r !== null) out.push({ item, ...r });
    }
    return out;
  }

  /**
   * Draw both pages, each with its own title copy. The inactive page rides
   * its own face — edge-on at rest (invisible), swinging through view during
   * a turn, exactly as the original's child transforms behave. The original
   * mounts an identical BUGGS! logo above *every* menu-bearing face of
   * `CubeGameObject` (`MainMenu.unity`: four `0_background_title` instances,
   * one per page), so a turn swings one logo out as the next page's swings
   * in and every page keeps a logo overhead. The pressed item plays its
   * `move` clip; everything else holds the rest pose.
   */
  draw(
    gl: WebGL2RenderingContext,
    renderer: ModelRenderer,
    state: ShellState,
    cubeModel: Mat4,
    viewProj: Mat4,
  ): void {
    const pressed = state.kind === "menu" ? state.pressed : null;
    const title = this.models.menuTitle;
    for (const page of ["main", "difficulty"] as const) {
      for (const { item, world } of itemWorlds(page)) {
        const model = this.models[ITEM_MODEL[item]];
        const isPressed = pressed !== null && pressed.item === item;
        // A missing clip name rests the pose; "move" plays once while pressed.
        const worlds = isPressed
          ? poseWorlds(model, "move", pressed.elapsed, false)
          : poseWorlds(model, "", 0, false);
        renderer.draw(gl, model, worlds, multiply(cubeModel, world), viewProj, true);
      }
      renderer.draw(
        gl,
        title,
        poseWorlds(title, "", 0, false),
        multiply(cubeModel, multiply(pageMatrix(page), TITLE_LOCAL)),
        viewProj,
        true,
      );
    }
  }
}
